import {
	type ComponentProps,
	type ComponentType,
	createContext,
	type PropsWithChildren,
	useContext,
} from 'react'
import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from '@cocoonspace/ui/components/dialog'
import {
	Drawer,
	DrawerClose,
	DrawerContent,
	DrawerDescription,
	DrawerFooter,
	DrawerHeader,
	DrawerTitle,
	DrawerTrigger,
} from '@cocoonspace/ui/components/drawer'
import {
	Popover,
	PopoverClose,
	PopoverContent,
	PopoverTrigger,
} from '@cocoonspace/ui/components/popover'
import { useIsMobile } from '@cocoonspace/ui/hooks/use-mobile'

interface DrawerDialogContext {
	isMobile: boolean
}

const mobileDesktopContext = createContext<DrawerDialogContext>(
	{} as DrawerDialogContext,
)

interface createDrawerDialogComponentParams<
	MobileComponentProps,
	DesktopComponentProps,
> {
	MobileComponent: ComponentType<PropsWithChildren<MobileComponentProps>>
	DesktopComponent: ComponentType<PropsWithChildren<DesktopComponentProps>>
	displayName: string
}

const createMobileDesktopComponent = <
	MobileComponentProps,
	DesktopComponentProps,
>({
	MobileComponent,
	DesktopComponent,
	displayName,
}: createDrawerDialogComponentParams<
	MobileComponentProps,
	DesktopComponentProps
>) => {
	const Component = ({
		children,
		...props
	}: PropsWithChildren<MobileComponentProps & DesktopComponentProps>) => {
		const { isMobile } = useContext(mobileDesktopContext)

		if (isMobile) {
			return (
				<MobileComponent {...(props as MobileComponentProps)}>
					{children}
				</MobileComponent>
			)
		}

		return (
			<DesktopComponent {...(props as DesktopComponentProps)}>
				{children}
			</DesktopComponent>
		)
	}

	Component.displayName = displayName

	return Component
}

export const DrawerDialogTrigger = createMobileDesktopComponent({
	DesktopComponent: DialogTrigger,
	displayName: 'DrawerDialogTrigger',
	MobileComponent: DrawerTrigger,
})

export const DrawerDialogClose = createMobileDesktopComponent({
	DesktopComponent: DialogClose,
	displayName: 'DrawerDialogClose',
	MobileComponent: DrawerClose,
})

export const DrawerDialogContent = createMobileDesktopComponent({
	DesktopComponent: DialogContent,
	displayName: 'DrawerDialogContent',
	MobileComponent: DrawerContent,
})

export const DrawerDialogHeader = createMobileDesktopComponent({
	DesktopComponent: DialogHeader,
	displayName: 'DrawerDialogHeader',
	MobileComponent: DrawerHeader,
})

export const DrawerDialogTitle = createMobileDesktopComponent({
	DesktopComponent: DialogTitle,
	displayName: 'DrawerDialogTitle',
	MobileComponent: DrawerTitle,
})

export const DrawerDialogDescription = createMobileDesktopComponent({
	DesktopComponent: DialogDescription,
	displayName: 'DrawerDialogDescription',
	MobileComponent: DrawerDescription,
})

export const DrawerDialogFooter = createMobileDesktopComponent({
	DesktopComponent: DialogFooter,
	displayName: 'DrawerDialogFooter',
	MobileComponent: DrawerFooter,
})

export const DrawerDialog = (
	props: PropsWithChildren<
		ComponentProps<typeof Dialog> & ComponentProps<typeof Drawer>
	>,
) => {
	const isMobile = useIsMobile()

	return (
		<mobileDesktopContext.Provider value={{ isMobile }}>
			{isMobile ? <Drawer {...props} /> : <Dialog {...props} />}
		</mobileDesktopContext.Provider>
	)
}

export const DrawerPopoverTrigger = createMobileDesktopComponent({
	DesktopComponent: PopoverTrigger,
	displayName: 'DrawerPopoverTrigger',
	MobileComponent: DrawerTrigger,
})

export const DrawerPopoverClose = createMobileDesktopComponent({
	DesktopComponent: PopoverClose,
	displayName: 'DrawerPopoverClose',
	MobileComponent: DrawerClose,
})

export const DrawerPopoverContent = createMobileDesktopComponent({
	DesktopComponent: PopoverContent,
	displayName: 'DrawerPopoverContent',
	MobileComponent: DrawerContent,
})

export const DrawerPopoverHeader = createMobileDesktopComponent({
	DesktopComponent: ({ children }) => children,
	displayName: 'DrawerPopoverHeader',
	MobileComponent: DrawerHeader,
})

export const DrawerPopoverTitle = createMobileDesktopComponent({
	DesktopComponent: ({ children }) => children,
	displayName: 'DrawerPopoverTitle',
	MobileComponent: DrawerTitle,
})

export const DrawerPopoverDescription = createMobileDesktopComponent({
	DesktopComponent: ({ children }) => children,
	displayName: 'DrawerPopoverDescription',
	MobileComponent: DrawerDescription,
})

export const DrawerPopover = (
	props: PropsWithChildren<
		ComponentProps<typeof Drawer> & ComponentProps<typeof Popover>
	>,
) => {
	const isMobile = useIsMobile()

	return (
		<mobileDesktopContext.Provider value={{ isMobile }}>
			{isMobile ? <Drawer {...props} /> : <Popover {...props} />}
		</mobileDesktopContext.Provider>
	)
}
